import {
  CheckCircle,
  LightbulbFilament,
  WarningCircle,
} from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import PhoneInput from "../../../components/SearchablePhoneInput";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import { formatAsMoney } from "../../../helpers/numbers";
import { getFriendlyBillsErrorMessage } from "../../../helpers/billsErrors";
import { BillNetwork } from "../../../hooks/useBillAirtimeProviders";
import useBillElectricityDiscos from "../../../hooks/useBillElectricityDiscos";
import useBillElectricityVerify from "../../../hooks/useBillElectricityVerify";
import useBillElectricityPurchase, {
  BillElectricityPurchasePayload,
} from "../../../hooks/useBillElectricityPurchase";
import BillPurchaseSuccess from "./BillPurchaseSuccess";
import Confirm from "../Confirm";
import BillPageHeader from "./components/BillPageHeader";
import SelectField from "./components/SelectField";
import ProviderSelectModal from "./components/ProviderSelectModal";
import BillSummaryCard from "./components/BillSummaryCard";
import SetPinPrompt from "./components/SetPinPrompt";

const WALLET_PAYMENT_OPTION_CODE = "004";
const MINIMUM_AMOUNT = 50;

const toNumber = (value: unknown) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value.replace(/[^\d.-]/g, "")) || 0;
  return 0;
};

const generateAgentReference = () =>
  `elect_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const ElectricityTemp = () => {
  const { user, refetch } = useAuth();

  const [selectedDisco, setSelectedDisco] = useState<BillNetwork | null>(null);
  const [meterNo, setMeterNo] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [pin, setPin] = useState("");
  const [payload, setPayload] = useState<BillElectricityPurchasePayload | null>(
    null
  );
  const [discoModalOpen, setDiscoModalOpen] = useState(false);
  // Meter must be verified before purchase; keep the value it was verified for
  // so changing the meter/disco forces a re-verify.
  const [verified, setVerified] = useState<{
    name: string;
    meter: string;
    disco: string;
  } | null>(null);

  const { discos, fetchingDiscos, isDiscosError, discosError } =
    useBillElectricityDiscos();

  const { verifyMeter, verifyingMeter } = useBillElectricityVerify({
    onSuccess(data) {
      setVerified({
        name: data?.data?.customer_name || "Verified customer",
        meter: meterNo,
        disco: selectedDisco?.code || "",
      });
    },
    onError(error) {
      setVerified(null);
      toast.error(getFriendlyBillsErrorMessage(error, "electricity"));
    },
  });

  const {
    purchaseElectricity,
    purchasingElectricity,
    isPurchaseError,
    purchaseError,
    purchaseResponse,
  } = useBillElectricityPurchase({
    onSuccess(data) {
      toast.success(data?.message || "Electricity purchase successful");
      refetch();
      setConfirming(false);
    },
    onError(error) {
      toast.error(getFriendlyBillsErrorMessage(error, "electricity"));
    },
  });

  const amountNumber = useMemo(() => Number(amount || 0), [amount]);
  const availableBalance = toNumber(user?.wallet?.available_balance);
  const isAmountValid = amountNumber >= MINIMUM_AMOUNT;
  const hasEnoughBalance = availableBalance >= amountNumber;
  const isVerified =
    !!verified &&
    verified.meter === meterNo &&
    verified.disco === (selectedDisco?.code || "");

  const canVerify =
    !!selectedDisco && meterNo.trim().length > 3 && !verifyingMeter;
  const canProceed =
    isVerified &&
    !!phoneNumber &&
    isAmountValid &&
    hasEnoughBalance &&
    !purchasingElectricity;

  const handleAmountChange = (value: string) =>
    setAmount(value.replace(/[^\d]/g, ""));

  const clearVerified = () => setVerified(null);

  const handleVerify = () => {
    if (!selectedDisco || !meterNo) {
      toast.error("Select a disco and enter your meter number.");
      return;
    }
    verifyMeter({ service_type: selectedDisco.code, meter_no: meterNo });
  };

  const handleSubmit = () => {
    if (!user?.pin_status) return;
    if (!isVerified) {
      toast.error("Verify your meter number first.");
      return;
    }
    if (!phoneNumber || !isAmountValid) {
      toast.error("Enter a phone number and an amount of at least NGN 50.");
      return;
    }
    if (!hasEnoughBalance) {
      toast.error("Insufficient wallet balance.");
      return;
    }

    setPayload({
      pin: "",
      service_type: selectedDisco!.code,
      meter_no: meterNo,
      phone: phoneNumber,
      amount: amountNumber,
      payment_option_code: WALLET_PAYMENT_OPTION_CODE,
      agentReference: generateAgentReference(),
    });
    setConfirming(true);
  };

  const authorizePurchase = useCallback(() => {
    if (!payload || !pin) return;
    purchaseElectricity({ ...payload, pin });
  }, [payload, pin, purchaseElectricity]);

  if (!user?.pin_status) {
    return (
      <SetPinPrompt
        redirect="/payments/bills/electricity"
        action="paying for electricity"
      />
    );
  }

  return (
    <div className="relative min-h-[70vh] px-5 py-8">
      {(fetchingDiscos || purchasingElectricity) && (
        <div className="fixed top-0 z-[120] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      {purchaseResponse && (
        <BillPurchaseSuccess
          title="Electricity purchase successful!"
          message={
            <>
              You have successfully bought{" "}
              <span className="font-bold">NGN {formatAsMoney(amountNumber)}</span>{" "}
              of electricity for meter{" "}
              <span className="font-bold">{meterNo}</span>.
              {purchaseResponse?.data?.token ? (
                <>
                  {" "}
                  Token:{" "}
                  <span className="font-bold">{purchaseResponse.data.token}</span>
                </>
              ) : null}
            </>
          }
        />
      )}

      {confirming && selectedDisco && (
        <Confirm
          title="Confirm Electricity Purchase"
          setConfirming={setConfirming}
          onAuthorized={authorizePurchase}
          onPinChange={setPin}
          description={
            <div>
              You are about to buy{" "}
              <span className="font-bold">NGN {formatAsMoney(amountNumber)}</span>{" "}
              of {selectedDisco.name} electricity for meter{" "}
              <span className="font-bold">{meterNo}</span>.
            </div>
          }
        />
      )}

      <div className="mx-auto max-w-5xl flex flex-col gap-6">
        <BillPageHeader
          title="Electricity"
          subtitle="Verify your meter, then enter an amount."
          icon={LightbulbFilament}
          walletBalance={availableBalance}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <section className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex flex-col gap-5">
            <SelectField
              label="Distribution company"
              placeholder="Select disco"
              icon={LightbulbFilament}
              value={selectedDisco?.name}
              onClick={() => setDiscoModalOpen(true)}
              loading={fetchingDiscos}
              loadingText="Loading discos..."
            />

            <div>
              <label className="text-sm font-medium text-gray-700">
                Meter number
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={meterNo}
                  onChange={(e) => {
                    setMeterNo(e.target.value.replace(/[^\d]/g, ""));
                    clearVerified();
                  }}
                  placeholder="Enter meter number"
                  className="flex-1 rounded-xl border border-gray-300 shadow-sm px-4 py-3 outline-none focus:outline focus:outline-deepBlue"
                />
                {!isVerified && (
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={!canVerify}
                    className="px-5 rounded-xl bg-deepBlue text-white font-medium disabled:opacity-50 shrink-0"
                  >
                    {verifyingMeter ? "Verifying..." : "Verify"}
                  </button>
                )}
              </div>
              {isVerified && (
                <div className="mt-2 flex items-center gap-2 text-green-600 text-sm">
                  <CheckCircle size={18} weight="fill" />
                  <span>{verified?.name}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Phone number
                </label>
                <PhoneInput
                  placeholder="Enter phone number"
                  international
                  defaultCountry="NG"
                  withCountryCallingCode
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  className="mt-2 focus:outline-none flex-1 flex items-center rounded-xl py-2 border border-gray-300 shadow-sm px-4 w-full focus-within:outline-deepBlue focus-within:outline"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Amount
                </label>
                <div className="mt-2 rounded-xl border border-gray-300 shadow-sm px-4 py-3 flex items-center gap-2 focus-within:outline focus-within:outline-deepBlue">
                  <span className="text-gray-500">NGN</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full outline-none"
                  />
                </div>
              </div>
            </div>

            {isDiscosError && (
              <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                {discosError?.message || "Unable to load discos."}
              </div>
            )}
          </section>

          <BillSummaryCard
            rows={[
              { label: "Disco", value: selectedDisco?.name || "Not selected" },
              { label: "Meter", value: meterNo || "Not entered" },
              {
                label: "Customer",
                value: isVerified ? verified!.name : "Not verified",
              },
            ]}
            amount={amountNumber}
            canProceed={canProceed}
            onProceed={handleSubmit}
            alerts={
              <>
                {!!amountNumber && !isAmountValid && (
                  <div className="rounded-xl bg-yellow-50 text-yellow-700 p-4 text-sm flex gap-2">
                    <WarningCircle size={20} weight="bold" />
                    <span>Minimum electricity amount is NGN 50.</span>
                  </div>
                )}
                {!!amountNumber && !hasEnoughBalance && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm flex gap-2">
                    <WarningCircle size={20} weight="bold" />
                    <span>
                      Your wallet balance is not enough for this purchase.
                    </span>
                  </div>
                )}
                {isPurchaseError && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                    {getFriendlyBillsErrorMessage(purchaseError, "electricity")}
                  </div>
                )}
              </>
            }
          />
        </div>
      </div>

      <ProviderSelectModal
        open={discoModalOpen}
        onClose={() => setDiscoModalOpen(false)}
        providers={discos}
        selectedCode={selectedDisco?.code}
        onSelect={(disco) => {
          setSelectedDisco(disco);
          clearVerified();
        }}
        label="disco"
        icon={LightbulbFilament}
      />
    </div>
  );
};

export default ElectricityTemp;
